import { CONFIG } from '../config';
import type { Bounds } from '../types';

type BorderDirection = 'left' | 'right' | 'top' | 'bottom';

export class Bullet {
    public x: number;
    public y: number;
    public vx: number;
    public vy: number;
    public radius: number;
    public color: string;
    public isAlive: boolean;
    public hasEnteredPlayArea: boolean;
    public onBoundaryHit: ((border: BorderDirection) => void) | null;

    // Split bullet 관련
    public canSplit: boolean;
    public splitCount: number;
    public maxSplits: number;

    // 보스 총알용 - 반사 없이 화면 밖으로 사라짐
    public noBounce: boolean = false;

    constructor(x: number, y: number, vx: number, vy: number, color?: string, canSplit: boolean = false) {
        this.x = x;
        this.y = y;
        this.vx = vx;
        this.vy = vy;
        this.radius = CONFIG.BULLET.RADIUS;
        this.color = color || this.getRandomColor();
        this.isAlive = true;
        this.hasEnteredPlayArea = false; // Track if bullet entered play area
        this.onBoundaryHit = null; // Callback for boundary collision

        // Split bullet 초기화
        this.canSplit = canSplit;
        this.splitCount = 0;
        this.maxSplits = 2; // 최대 2번 분열
    }

    /**
     * 풀링을 위한 리셋 메서드
     */
    reset(x: number, y: number, vx: number, vy: number, color?: string, canSplit: boolean = false): void {
        this.x = x;
        this.y = y;
        this.vx = vx;
        this.vy = vy;
        this.radius = CONFIG.BULLET.RADIUS;
        this.color = color || this.getRandomColor();
        this.isAlive = true;
        this.hasEnteredPlayArea = false;
        this.onBoundaryHit = null;
        this.canSplit = canSplit;
        this.splitCount = 0;
        this.noBounce = false;
    }

    getRandomColor(): string {
        // All bullets are white for Podoui gradient background
        return '#FFFFFF';
    }

    update(deltaTime: number, canvasWidth: number = CONFIG.CANVAS_WIDTH, canvasHeight: number = CONFIG.CANVAS_HEIGHT): void {
        this.x += this.vx * deltaTime;
        this.y += this.vy * deltaTime;

        // Play area boundaries (inside the border)
        const margin = CONFIG.PLAY_AREA.MARGIN;
        const minX = margin;
        const maxX = canvasWidth - margin;
        const minY = margin;
        const maxY = canvasHeight - margin;

        // Check if bullet has entered the play area at least once
        // 총알이 완전히 플레이 영역 안에 들어왔을 때만 true로 설정 (반지름 고려)
        if (!this.hasEnteredPlayArea) {
            if (this.x - this.radius >= minX && this.x + this.radius <= maxX &&
                this.y - this.radius >= minY && this.y + this.radius <= maxY) {
                this.hasEnteredPlayArea = true;
            }
        }

        // If bullet has entered play area, reflect off borders (unless noBounce)
        if (this.hasEnteredPlayArea) {
            // noBounce 총알은 화면 밖으로 나가면 사라짐
            if (this.noBounce) {
                const cleanupMargin = 50;
                if (this.x < minX - cleanupMargin || this.x > maxX + cleanupMargin ||
                    this.y < minY - cleanupMargin || this.y > maxY + cleanupMargin) {
                    this.isAlive = false;
                }
                return;
            }

            let hitBorder: BorderDirection | null = null;

            // Left border
            if (this.x - this.radius < minX) {
                this.x = minX + this.radius;
                this.vx = Math.abs(this.vx); // Reflect right
                hitBorder = 'left';
            }
            // Right border
            if (this.x + this.radius > maxX) {
                this.x = maxX - this.radius;
                this.vx = -Math.abs(this.vx); // Reflect left
                hitBorder = 'right';
            }
            // Top border
            if (this.y - this.radius < minY) {
                this.y = minY + this.radius;
                this.vy = Math.abs(this.vy); // Reflect down
                hitBorder = 'top';
            }
            // Bottom border
            if (this.y + this.radius > maxY) {
                this.y = maxY - this.radius;
                this.vy = -Math.abs(this.vy); // Reflect up
                hitBorder = 'bottom';
            }

            // Trigger sound callback if boundary was hit
            if (hitBorder !== null && this.onBoundaryHit !== null) {
                this.onBoundaryHit(hitBorder);
            }
        } else {
            // Before entering play area, check if too far off-screen
            const cleanupMargin = 100;
            if (this.x < -cleanupMargin || this.x > canvasWidth + cleanupMargin ||
                this.y < -cleanupMargin || this.y > canvasHeight + cleanupMargin) {
                this.isAlive = false;
            }
        }
    }

    render(ctx: CanvasRenderingContext2D): void {
        ctx.fillStyle = this.color;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
        ctx.fill();
    }

    getBounds(): Bounds {
        return {
            x: this.x,
            y: this.y,
            radius: this.radius
        };
    }

    /**
     * 총알 분열 - 2개의 작은 총알 반환
     */
    split(): Bullet[] | null {
        if (!this.canSplit || this.splitCount >= this.maxSplits) {
            return null;
        }

        // 현재 속도의 방향에서 ±45도 방향으로 분열
        const speed = Math.sqrt(this.vx * this.vx + this.vy * this.vy);
        const angle = Math.atan2(this.vy, this.vx);
        const spreadAngle = Math.PI / 4; // 45도

        const bullets: Bullet[] = [];

        for (let i = 0; i < 2; i++) {
            const newAngle = angle + (i === 0 ? -spreadAngle : spreadAngle);
            const newVx = Math.cos(newAngle) * speed;
            const newVy = Math.sin(newAngle) * speed;

            const bullet = new Bullet(this.x, this.y, newVx, newVy, '#FFAAAA', true);
            bullet.splitCount = this.splitCount + 1;
            bullet.hasEnteredPlayArea = true; // 이미 플레이 영역 내에서 분열됨
            bullet.radius = this.radius * 0.8; // 약간 작게
            bullets.push(bullet);
        }

        return bullets;
    }

    /**
     * 분열 총알인지 확인
     */
    isSplitBullet(): boolean {
        return this.canSplit && this.splitCount > 0;
    }
}
